import { Link } from "react-router-dom";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import { MapPin, Briefcase, Building2, CheckCircle2, AlertCircle, ArrowRight, ExternalLink } from "lucide-react";

const REGIONS = [
  { name: "Northern Territory DAMA", coverage: "Whole of NT (Darwin and regional)", notes: "Australia's first DAMA — established 2007. Broad occupation list including hospitality, construction, agriculture and trades." },
  { name: "Goldfields DAMA (WA)", coverage: "Kalgoorlie-Boulder & surrounds", notes: "Mining, engineering, hospitality, construction and trades." },
  { name: "Great South Coast DAMA (VIC)", coverage: "Warrnambool & western Victoria", notes: "Dairy, agriculture, meat processing and rural services." },
  { name: "Adelaide Technology & Innovation Advancement DAMA (SA)", coverage: "Metropolitan Adelaide", notes: "Defence, space, AI, advanced manufacturing and technology occupations." },
  { name: "South Australian Regional Workforce DAMA", coverage: "Regional South Australia", notes: "Agriculture, aged care, hospitality, manufacturing and trades." },
  { name: "Orana DAMA (NSW)", coverage: "Dubbo, Wellington and Orana region", notes: "Skilled labour shortages in trades, healthcare and agriculture." },
  { name: "Far North Queensland DAMA", coverage: "Cairns and Far North Queensland", notes: "Tourism, hospitality, healthcare, construction and agriculture." },
  { name: "Townsville DAMA (QLD)", coverage: "Townsville region", notes: "Healthcare, defence-related industries, trades and hospitality." },
  { name: "East Kimberley DAMA (WA)", coverage: "Kununurra, Wyndham & East Kimberley", notes: "Tourism, agriculture, healthcare and trades." },
  { name: "Pilbara DAMA (WA)", coverage: "Pilbara region", notes: "Resources, construction, engineering and trades." },
];

const BENEFITS = [
  "Access to occupations not on the standard Core Skills Occupation List (CSOL) — including semi-skilled and specialist roles.",
  "Salary concessions on the Temporary Skilled Migration Income Threshold (TSMIT) for eligible occupations.",
  "English language concessions for eligible occupations.",
  "Age concession — up to 55 years for certain employer-sponsored permanent pathways.",
  "Pathway to permanent residence after qualifying period (typically via Subclass 186 or 191).",
  "Greater flexibility for employers in regional and remote areas of Australia.",
];

const STEPS = [
  { n: "01", title: "Employer endorsement", desc: "Your prospective employer applies to the relevant Designated Area Representative (DAR) for endorsement of a labour need under the DAMA." },
  { n: "02", title: "Labour Agreement nomination", desc: "Once endorsed, the employer lodges a nomination with the Department of Home Affairs under the Labour Agreement stream." },
  { n: "03", title: "Visa application", desc: "You lodge a Subclass 482 (Skills in Demand — Labour Agreement stream), Subclass 494 (regional) or Subclass 186 (Labour Agreement stream) — depending on the DAMA and occupation." },
  { n: "04", title: "Pathway to PR", desc: "Many DAMA pathways lead to permanent residence after a qualifying period of employment. We map the right route from day one." },
];

export default function DamaVisas() {
  return (
    <div>
      <PageHeader
        eyebrow="Designated Area Migration Agreements"
        title="DAMA Visas — Regional Skilled Pathways to Australia."
        subtitle="Designated Area Migration Agreements give regional Australian employers access to overseas workers in occupations not available under standard skilled visa programs — with concessions on age, English and salary, and a pathway to permanent residence."
        image="/assets/sydney.jpg"
      />

      {/* Intro */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-600 leading-relaxed text-[15px] space-y-5">
          <p>
            A <strong className="text-[#29324A]">Designated Area Migration Agreement (DAMA)</strong> is a formal labour agreement between the Australian Government and a regional, state or territory authority known as a Designated Area Representative (DAR). DAMAs allow employers in specific regional areas to sponsor overseas workers in occupations and at skill levels not typically available under the standard Skills in Demand (Subclass 482), Skilled Employer Sponsored Regional (Subclass 494) or Employer Nomination Scheme (Subclass 186) programs.
          </p>
          <p>
            DAMAs are negotiated to address genuine labour market shortages in regional areas where employers cannot find suitably skilled Australian workers. Each DAMA has its own occupation list, concession framework and eligibility rules.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 md:py-16 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> Why DAMA matters
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-8">
            Key benefits of a DAMA pathway
          </h2>
          <div className="grid md:grid-cols-2 gap-px bg-slate-200">
            {BENEFITS.map((b, i) => (
              <div key={i} className="bg-white p-5 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-[#C43A89] mt-0.5 shrink-0" />
                <span className="text-sm text-slate-700 leading-relaxed">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Current DAMAs */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> Designated Area Representatives
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-3">
            Current DAMAs across Australia
          </h2>
          <p className="text-sm text-slate-600 max-w-3xl mb-10">
            DAMAs are active across each State and Territory. Each agreement has its own occupation list and concession framework — please speak with us about the right DAMA for your occupation and goals.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {REGIONS.map((r, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5 hover:border-[#29324A] transition-colors">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-[#29324A] mt-0.5 shrink-0" />
                  <div>
                    <h3 className="font-display text-lg text-[#29324A] leading-tight">{r.name}</h3>
                    <div className="text-[11px] tracking-wider uppercase text-[#C43A89] font-bold mt-1">{r.coverage}</div>
                    <p className="text-sm text-slate-600 mt-2 leading-relaxed">{r.notes}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-7 text-xs text-slate-500 italic">
            DAMAs and their occupation lists are reviewed and updated by the relevant Designated Area Representative. Please confirm current arrangements with us before relying on any specific concession.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-14 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> How DAMA works
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            Four steps from employer endorsement to PR
          </h2>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STEPS.map((s, i) => (
              <div key={i} className="bg-white p-6 relative">
                <span aria-hidden className="absolute top-0 left-0 h-1 w-12 bg-[#C43A89]" />
                <div className="font-display text-3xl text-[#29324A] leading-none">{s.n}</div>
                <h3 className="font-display text-lg text-[#29324A] mt-3 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For whom */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-10 items-start">
          <div className="bg-[#F8F9FA] p-7 border-l-4 border-[#29324A]">
            <Building2 className="h-7 w-7 text-[#29324A] mb-4" />
            <h3 className="font-display text-2xl text-[#29324A] mb-3">For Employers</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Access overseas talent for occupations not on the standard CSOL.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Salary, English and age concessions for eligible roles.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Endorsement support from the relevant DAR through to nomination.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" /> Long-term workforce planning across multiple positions.</li>
            </ul>
          </div>
          <div className="bg-[#F8F4FB] p-7 border-l-4 border-[#C43A89]">
            <Briefcase className="h-7 w-7 text-[#C43A89] mb-4" />
            <h3 className="font-display text-2xl text-[#29324A] mb-3">For Workers</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Pathway into Australia in occupations otherwise unavailable.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Concessions on English requirements where applicable.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Up to 55 years of age for eligible permanent pathways.</li>
              <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" /> Pathway to permanent residence after qualifying period.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Common visa subclasses linked to DAMA */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl md:text-3xl text-[#29324A] mb-6">Visa subclasses used under a DAMA</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { sc: "Subclass 482", title: "Skills in Demand — Labour Agreement Stream", desc: "Temporary skilled visa under a DAMA — typically 2 to 4 years." },
              { sc: "Subclass 494", title: "Skilled Employer Sponsored Regional", desc: "Provisional regional visa with PR pathway via Subclass 191." },
              { sc: "Subclass 186", title: "ENS — Labour Agreement Stream", desc: "Direct permanent residence pathway where the DAMA permits." },
            ].map((v, i) => (
              <div key={i} className="bg-white p-6 border border-slate-200">
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-2">{v.sc}</div>
                <h3 className="font-display text-lg text-[#29324A] mb-2 leading-tight">{v.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AlertCircle className="h-9 w-9 text-[#C43A89] mx-auto mb-5" />
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
            Explore a DAMA pathway with our migration team.
          </h2>
          <p className="mt-4 text-sm text-slate-600 leading-relaxed">
            DAMAs are complex, region-specific and evidence-driven. Our Registered Migration Agents can identify the right DAMA, occupation and subclass for your circumstances — and walk through the employer endorsement and visa lodgement journey end-to-end.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/book-consultation"
              data-testid="dama-book-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3.5 px-7 transition-colors btn-shimmer text-xs uppercase tracking-wider"
            >
              Book a DAMA Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href="https://immi.homeaffairs.gov.au/visas/employing-and-sponsoring-someone/sponsoring-workers/nominating-a-position/labour-agreements/designated-area-migration-agreements"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white font-semibold py-3.5 px-7 transition-colors text-xs uppercase tracking-wider"
            >
              Department of Home Affairs <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            Information on this page is general in nature. DAMA arrangements, occupation lists and concessions are determined by individual Designated Area Representatives and may change at any time. Please speak with a Registered Migration Agent for personalised advice.
          </Disclaimer>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
